import { type BaseRoles } from "@shared/Roles";
import {
  Entity,
  remult,
  repo,
  type ClassType,
  type EntityOptions,
} from "remult";
import { allowPerRole, type AllowRole } from "./entity-security/allow-per-role";
import {
  preProcessFilterPerRoles,
  type RoleCheck,
} from "./entity-security/preprocess-filter-for-role";
import "reflect-metadata";
import { runWithOverrideSecurity } from "./entity-security/run-with-override-security";
/**
 * A function that defines role-based access logic for a CRUD operation.
 *
 * @template Entity The Remult entity type.
 */
export type CrudAllow<Entity> = (allowRole: AllowRole<Entity>) => void;

/**
 * Extended entity configuration that supports role-based access control (RBAC),
 * lifecycle hooks, and custom filters and effects.
 *
 * @template Entity The Remult entity type.
 */
export interface FullEntityConfig<Entity>
  extends Omit<EntityOptions<Entity>, "saved" | "deleted"> {
  /**
   * Override the roles used in `allowPerRole` (useful for testing or simulation).
   */
  __userRoles: BaseRoles[];

  /**
   * Disables security console logs during development.
   */
  __preventLogger: boolean;

  /**
   * Access logic for insert operations (`event.isNew === true`).
   */
  allowInsertPerRole: CrudAllow<Entity>;

  /**
   * Access logic for update operations (`event.isNew === false`).
   */
  allowUpdatePerRole: CrudAllow<Entity>;

  /**
   * Access logic for delete operations.
   */
  allowDeletePerRole: CrudAllow<Entity>;

  /**
   * Shorthand for setting both allowUpdatePerRole and allowDeletePerRole
   */
  allowDeleteAndUpdatePerRole: CrudAllow<Entity>;

  /**
   * Hook executed after an entity has been inserted.
   */
  onInsert: EntityOptions<Entity>["saved"];

  /**
   * Hook executed after an entity has been updated.
   */
  onUpdate: EntityOptions<Entity>["saved"];

  /**
   * Hook executed after an entity has been inserted or updated.
   */
  onSave: EntityOptions<Entity>["saved"];

  /**
   * Hook executed after an entity has been deleted.
   */
  onDelete: EntityOptions<Entity>["deleted"];

  /**
   * Filter logic for `apiPreprocessFilter`, defined per role.
   * This controls row-level access based on roles.
   */
  apiPrefilterPerRole: (filter: RoleCheck<Entity>) => void | Promise<void>;
}

export type HyperEntityConfig<Entity> = Partial<FullEntityConfig<Entity>>

/**
 * Internal helper that builds Remult's `EntityOptions` by injecting role-based access control,
 * lifecycle handling, and dynamic filters based on the provided `EntityConfig`.
 *
 * @template Entity The Remult entity type.
 * @param config A partial `EntityConfig` object.
 * @returns A fully constructed `EntityOptions` object to pass into `@Entity()`.
 */
function buildEntityConfig<Entity>(
  config: HyperEntityConfig<Entity>,
  target: ClassType<Entity>,
): EntityOptions<Entity> {
  const {
    allowDeletePerRole,
    allowInsertPerRole,
    allowUpdatePerRole,
    saving,
    deleting,
    onSave,
    onInsert,
    onUpdate,
    onDelete,
    apiPrefilterPerRole,
    apiPreprocessFilter,
    __userRoles = remult.user?.roles as BaseRoles[],
    __preventLogger = false,
    allowDeleteAndUpdatePerRole,
    ...rest
  } = config;

  if (
    allowDeleteAndUpdatePerRole &&
    (allowUpdatePerRole || allowDeletePerRole)
  ) {
    throw new Error(
      `Invalid config values for entity!, received both 'allowDeleteAndUpdatePerRole' and '${allowUpdatePerRole ? "allowUpdatePerRole" : "allowDeletePerRole"}'`,
    );
  }

  return {
    /**
     * Lifecycle hook: runs before save (insert/update).
     * Enforces access permissions based on defined `allowX` logic.
     */
    async saving(entity, event) {
      const isInsert = event.isNew;
      const shouldOverrideSecurity =
        remult.context.shouldOverrideSecurity ?? false;
      const metadata = repo(target).metadata;

      if (
        !shouldOverrideSecurity &&
        ((isInsert && !metadata.apiInsertAllowed) ||
          (!isInsert && !metadata.apiUpdateAllowed))
      ) {
        throw new Error(
          `[Entity Security - not allowed ${isInsert ? "INSERT" : "UPDATE"}] "${event.metadata.dbName}"`,
        );
      } else if (!shouldOverrideSecurity) {
        const isAllowed = await allowPerRole<Entity>(
          (allow) => {
            if (isInsert) { allowInsertPerRole?.(allow) } else { (allowUpdatePerRole ?? allowDeleteAndUpdatePerRole)?.(allow) }
          },
          { userRoles: __userRoles },
        )(entity, event);

        if (!isAllowed) {
          throw new Error(
            `[Entity Security - not allowed ${isInsert ? "INSERT" : "UPDATE"}] "${event.metadata.dbName}"`,
          );
        }
      }

      await runWithOverrideSecurity(async () => await saving?.(entity, event));
    },

    /**
     * Lifecycle hook: runs before delete.
     * Enforces access permissions based on defined `allowX` logic.
     */
    async deleting(entity, event) {
      const metadata = repo(target).metadata;
      const shouldOverrideSecurity =
        remult.context.shouldOverrideSecurity ?? false;
      if (!shouldOverrideSecurity && !metadata.apiDeleteAllowed) {
        throw new Error(
          `[Entity Security - not allowed DELETE] "${event.metadata.dbName}"`,
        );
      } else if (!shouldOverrideSecurity) {
        const isAllowed = await allowPerRole<Entity>(
          (allow) => {
            (allowDeletePerRole ?? allowDeleteAndUpdatePerRole)?.(allow);
          },
          { userRoles: __userRoles },
        )(entity, event);

        if (!isAllowed) {
          throw new Error(
            `[Entity Security - not allowed DELETE] "${event.metadata.dbName}"`,
          );
        }
      }

      await runWithOverrideSecurity(
        async () => await deleting?.(entity, event),
      );
    },

    /**
     * Lifecycle hook: runs after entity has been saved.
     * Executes both user-defined events and configured `saveEffects`.
     */
    async saved(entity, e) {
      await onSave?.(entity, e);

      await runWithOverrideSecurity(async () =>
        e.isNew ? await onInsert?.(entity, e) : await onUpdate?.(entity, e),
      );
    },

    /**
     * Lifecycle hook: runs after delete.
     */
    deleted: onDelete,

    /**
     * Row-level security for API reads.
     * Allows combining client filters with role-based restrictions.
     */
    apiPreprocessFilter: async (filter, event) => {
      let processed = (await apiPreprocessFilter?.(filter, event)) ?? filter;

      if (apiPrefilterPerRole) {
        processed = await preProcessFilterPerRoles<Entity>(
          async (roleCheck) => {
            await apiPrefilterPerRole(roleCheck);
          },
          {
            userRoles: __userRoles,
            disableDevelopmentSecurityLogger: __preventLogger,
          },
        )(processed, event);
      }

      return processed;
    },

    ...rest,
  };
}

/**
 * A wrapper around Remult's `@Entity()` decorator that adds robust,
 * configurable role-based security, lifecycle logic, and filter injection.
 *
 * Use this to declare secure entities with permission checks and effects in one place.
 *
 * @template Entity The Remult entity type (instance, not class).
 * @param key The database table name.
 * @param config Optional entity config object that defines role-based access and effects.
 * @returns A fully-configured Remult `@Entity()` decorator.
 *
 * @example
 * ```ts
 * @HyperEntity("tasks", {
 *   allowInsert: allow => allow(Roles.Admin, true),
 *   allowUpdate: allow => allow(Roles.Owner, (task) => task.ownerId === remult.user?.id),
 *   allowDelete: allow => allow(Roles.Admin, true),
 *   saveEffects: [
 *     createAuditLogEffect("status"), // react to field changes
 *   ],
 *   apiPrefilterPerRole: filter => {
 *     filter(Roles.Admin, true);
 *     filter(Roles.User, { ownerId: remult.user?.id });
 *   }
 * })
 * export class Task {
 *   ...
 * }
 * ```
 */
export default function HyperEntity<Entity>(
  key: string,
  config: HyperEntityConfig<
    Entity extends ClassType<any> ? InstanceType<Entity> : Entity
  > = {},
) {
  return function (target: ClassType<Entity>) {
    const remultEntityDecorator = Entity(
      key,
      buildEntityConfig(config, target as any),
    );
    if (!(target as any).hyperFilter) {
      throw new Error(
        `Entity must have hyperFilter defined!! Received undefined in ${target.name}`,
      );
    }

    Reflect.defineMetadata(HYPER_ENTITY_CONFIG_METADATA_KEY, config, target);
    return remultEntityDecorator(target);
  };
}

const HYPER_ENTITY_CONFIG_METADATA_KEY = "hyper-entity-config";

export function getHyperEntityConfig<Entity>(
  entity: ClassType<Entity>,
): HyperEntityConfig<Entity> {
  return Reflect.getMetadata(HYPER_ENTITY_CONFIG_METADATA_KEY, entity);
}
